import { useEffect, useRef, useState } from "react";

interface EditableTextProps {
  value: string;
  onCommit: (value: string) => void | Promise<void>;
  /** Render as multi-line textarea instead of single-line input. */
  multiline?: boolean;
  placeholder?: string;
  className?: string;
  /** Tailwind classes applied to the display element. */
  displayClassName?: string;
  inputClassName?: string;
  ariaLabel?: string;
  /** Cap the draft length, so an over-long value is stopped here rather than
   *  by the server's validation on commit. */
  maxLength?: number;
  /** Mount already in edit mode (e.g. a just-created item's title). */
  startInEdit?: boolean;
  /**
   * Optional controlled mode. Pass both to let a parent open the editor — e.g. a
   * card header that wants its whole area, not just the text, to be clickable.
   * Omit both and the component manages its own state as before.
   */
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
}

/**
 * Click-to-edit text. Click the display to edit; Enter (or blur) commits,
 * Escape cancels. For multiline, Cmd/Ctrl+Enter commits.
 */
export function EditableText({
  value,
  onCommit,
  multiline = false,
  placeholder = "Click to edit",
  className = "",
  displayClassName = "",
  inputClassName = "",
  ariaLabel,
  maxLength,
  startInEdit = false,
  editing: controlledEditing,
  onEditingChange,
}: EditableTextProps) {
  const [uncontrolledEditing, setUncontrolledEditing] = useState(startInEdit);
  const editing = controlledEditing ?? uncontrolledEditing;
  const setEditing = (next: boolean) => {
    setUncontrolledEditing(next);
    onEditingChange?.(next);
  };
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (editing) {
      // Seed the draft here, not in the click handler: in controlled mode the
      // parent opens the editor, so the handler never runs and the draft would
      // otherwise still hold whatever it did last time.
      setDraft(value);
      const el = inputRef.current;
      el?.focus();
      el?.select();
    }
    // `value` is deliberately not a dep — re-seeding mid-edit (the card poll
    // refetches every 10s) would discard what the user is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  if (!editing) {
    const display = value || placeholder;
    const isEmpty = !value;
    return (
      <span
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            setDraft(value);
            setEditing(true);
          }
        }}
        className={`${displayClassName} ${className} ${
          isEmpty ? "text-slate-400 italic" : ""
        } cursor-text rounded px-0.5 hover:bg-slate-100`}
      >
        {multiline ? <span className="whitespace-pre-wrap">{display}</span> : display}
      </span>
    );
  }

  function commit() {
    const next = draft.trim();
    setEditing(false);
    if (next !== value) void onCommit(next);
  }

  function cancel() {
    setEditing(false);
    setDraft(value);
  }

  const sharedProps = {
    ref: inputRef as never,
    value: draft,
    onChange: (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => setDraft(e.target.value),
    onBlur: commit,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (!multiline && e.key === "Enter") {
        e.preventDefault();
        commit();
      } else if (e.key === "Escape") {
        // cancel the edit only — don't let an enclosing modal treat this
        // Escape as "close"
        e.preventDefault();
        e.stopPropagation();
        cancel();
      } else if (multiline && e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        commit();
      }
    },
    "aria-label": ariaLabel,
    maxLength,
    className: `${inputClassName} ${className} w-full rounded border border-blue-400 px-1 py-0.5 outline-none ring-2 ring-blue-100`,
  };

  return multiline ? (
    <textarea {...(sharedProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)} rows={4} />
  ) : (
    <input {...(sharedProps as React.InputHTMLAttributes<HTMLInputElement>)} />
  );
}